import { createAsyncThunk } from '@reduxjs/toolkit';
import { filesToFileData } from 'utils/file-upload';
import { importFiles } from './../index';
import PropertiesResource from 'api/service/PropertiesResource';
import FilesServiceResource from 'api/service/FilesResource';
import { registerUploadAbortController, unregisterUploadAbortController } from './upload-cancel-manager';

const isUploadCancelled = (state, fileId) => {
  const file = state.files.importedFiles.find((item) => item.id === fileId);
  return file?.status === 'cancelled';
};

const cleanupCancelledUpload = async ({ oid = '', id = '' }) => {
  const fileIdentifier = oid || id;

  if (!fileIdentifier) {
    return;
  }

  const filesResource = new FilesServiceResource();
  await filesResource.file(fileIdentifier).delete();
};

export const uploadFileMeta = createAsyncThunk('files/uploadFileMeta', async (initialData, { rejectWithValue }) => {
  const { id, file, parent, signal } = initialData;
  const filesResource = new FilesServiceResource();
  const response = await filesResource.upload(file, parent, { signal });
  const { status, data } = response || {};
  if (status === 200) {
    return { status, data, id, type: file.type };
  }
  return rejectWithValue(id);
});

export const uploadFileBlob = createAsyncThunk('files/uploadFileBlob', async (initialData, { rejectWithValue }) => {
  const { id, uploadUrl, file, signal } = initialData;
  const filesResource = new FilesServiceResource();
  const response = await filesResource.uploadFile(uploadUrl, file, { signal });
  const { status } = response || {};
  if (status === 201) {
    return { status, id };
  }
  return rejectWithValue(id);
});

export const uploadFileRefresh = createAsyncThunk(
  'files/uploadFileRefresh',
  async (initialData, { rejectWithValue }) => {
    const { id, oid, signal } = initialData;
    const filesResource = new FilesServiceResource();
    const response = await filesResource.file(oid).refresh({ signal });
    const { status, data } = response || {};
    if (status === 200) {
      return { id, status, data };
    }
    return rejectWithValue(id);
  }
);

export const uploadFiles = createAsyncThunk('files/uploadFiles', async (initialData, { dispatch, getState }) => {
  const { files, parent, parentFileId, location } = initialData;

  if (!files || files.length === 0) {
    console.warn('No files provided for upload.');
    return;
  }

  let propertyHasPreview = false;

  const propertiesResource = new PropertiesResource();

  if (location.startsWith('/properties/')) {
    // get property preview file id
    const { data } = await propertiesResource.get(parentFileId);
    propertyHasPreview = data?.preview_file_id || false;
  }

  const { importedFiles, importedFilesData } = filesToFileData(files, parent, location);

  dispatch(importFiles(importedFiles));

  const uploadPromises = importedFilesData.map(async (file) => {
    const fileUploadController = new AbortController();
    registerUploadAbortController(file.id, fileUploadController);

    let uploadedFileRef = {
      id: '',
      oid: '',
    };

    try {
      if (isUploadCancelled(getState(), file.id) || fileUploadController.signal.aborted) {
        await cleanupCancelledUpload(uploadedFileRef);
        return 'upload cancelled';
      }

      // Step 1: Upload file metadata
      const { payload: uploadFileMetaResponse } = await dispatch(
        uploadFileMeta({ id: file.id, file: file.fileData, parent, signal: fileUploadController.signal })
      );

      if (uploadFileMetaResponse?.status === 200) {
        uploadedFileRef = {
          id: uploadFileMetaResponse.data?.id || '',
          oid: uploadFileMetaResponse.data?.oid || '',
        };

        if (isUploadCancelled(getState(), file.id) || fileUploadController.signal.aborted) {
          await cleanupCancelledUpload(uploadedFileRef);
          return 'upload cancelled';
        }

        // Step 2: Upload the actual file to the blob/storage URL
        const { payload: uploadFileBlobResponse } = await dispatch(
          uploadFileBlob({
            id: file.id,
            uploadUrl: uploadFileMetaResponse.data.upload_url,
            file: file.fileData,
            signal: fileUploadController.signal,
          })
        );

        if (isUploadCancelled(getState(), file.id) || fileUploadController.signal.aborted) {
          await cleanupCancelledUpload(uploadedFileRef);
          return 'upload cancelled';
        }

        if (uploadFileBlobResponse?.status === 201) {
          if (isUploadCancelled(getState(), file.id) || fileUploadController.signal.aborted) {
            await cleanupCancelledUpload(uploadedFileRef);
            return 'upload cancelled';
          }

          // Step 3: Refresh the file to get final details
          const { payload: uploadFileRefreshResponse } = await dispatch(
            uploadFileRefresh({
              id: file.id,
              oid: uploadFileMetaResponse.data.oid,
              signal: fileUploadController.signal,
            })
          );

          if (isUploadCancelled(getState(), file.id) || fileUploadController.signal.aborted) {
            await cleanupCancelledUpload(uploadedFileRef);
            return 'upload cancelled';
          }

          if (uploadFileRefreshResponse?.status === 200) {
            return uploadFileRefreshResponse.data;
          }
          return 'uploaded file refresh failed';
        }
        return 'upload file blob failed';
      }
      return 'upload file metadata failed';
    } finally {
      unregisterUploadAbortController(file.id);
    }
  });

  // Wait for all uploads to finish
  const uploadedFiles = await Promise.all(uploadPromises);

  if (location.startsWith('/properties/')) {
    // set first uploaded image as property preview photo
    const validImageType = ['image/jpeg', 'image/png', 'image/jpg'];
    const firstImageUploaded = uploadedFiles.find(
      (file) => file?.content_type && validImageType.includes(file.content_type)
    );
    if (!propertyHasPreview && firstImageUploaded) {
      // set file as property preview photo
      await propertiesResource.preview(parentFileId).set(firstImageUploaded.oid);
    }
  }

  return uploadedFiles; // Returns an array of successful responses
});
